<template>
  <Bar :options="chartOptions" :data="chartData" />
</template>
<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { useThemeHelper } from "@/theme/themeHelper"
  import { Bar } from "vue-chartjs"
  import {
    Chart as ChartJS,
    BarElement,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    ChartData,
    ChartOptions,
  } from "chart.js"

  ChartJS.register(
    BarElement,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
  )

  interface BarDataSet {
    label: string
    data: number[]
    backgroundColor: string
    yAxisID?: string
  }

  interface LineDataSet {
    label: string
    data: number[]
    borderColor: string
    backgroundColor?: string
    borderWidth?: number
    pointRadius?: number
    fill?: boolean
    yAxisID?: string
  }

  interface SecondaryYAxis {
    title?: string
    tickColor?: string
  }

  export default defineComponent({
    components: { Bar },
    props: {
      labels: {
        type: Array as PropType<string[]>,
        required: true,
      },
      barDataSet: {
        type: Object as PropType<BarDataSet>,
        required: true,
      },
      lineDataSet: {
        type: Object as PropType<LineDataSet>,
        default: null,
      },
      secondaryYAxis: {
        type: Object as PropType<SecondaryYAxis>,
        default: null,
      },
      yAxisTitle: {
        type: String,
        default: undefined,
      },
      tooltipLabelCallback: {
        type: Function as PropType<
          (context: {
            dataset: { label?: string }
            parsed: { y: number | null }
          }) => string
        >,
        default: undefined,
      },
    },
    data() {
      return {
        currentTheme: useThemeHelper(),
      }
    },
    computed: {
      chartData(): ChartData<"bar"> {
        const datasets: ChartData<"bar">["datasets"] = [
          {
            label: this.barDataSet.label,
            data: this.barDataSet.data,
            backgroundColor: this.barDataSet.backgroundColor,
            yAxisID: this.barDataSet.yAxisID ?? "y",
            order: 2,
          },
        ]

        if (this.lineDataSet) {
          datasets.push({
            // @ts-expect-error — Chart.js mixed chart: line dataset on a Bar canvas
            type: "line",
            label: this.lineDataSet.label,
            data: this.lineDataSet.data,
            borderColor: this.lineDataSet.borderColor,
            backgroundColor: this.lineDataSet.backgroundColor,
            borderWidth: this.lineDataSet.borderWidth ?? 2,
            pointRadius: this.lineDataSet.pointRadius ?? 3,
            fill: this.lineDataSet.fill ?? false,
            yAxisID: this.lineDataSet.yAxisID ?? "y1",
            order: 1,
          })
        }

        return { labels: this.labels, datasets }
      },
      chartOptions(): ChartOptions<"bar"> {
        const textColor = this.currentTheme.colors["on-background"]
        const gridColor = this.currentTheme.colors.border

        const scales: ChartOptions<"bar">["scales"] = {
          y: {
            position: "left",
            title: this.yAxisTitle
              ? { display: true, text: this.yAxisTitle, color: textColor }
              : undefined,
            ticks: { color: textColor },
            grid: { color: gridColor },
          },
          x: {
            ticks: { color: textColor },
            grid: { color: gridColor },
          },
        }

        if (this.secondaryYAxis) {
          scales.y1 = {
            position: "right",
            title: this.secondaryYAxis.title
              ? {
                  display: true,
                  text: this.secondaryYAxis.title,
                  color: textColor,
                }
              : undefined,
            ticks: { color: textColor },
            grid: { drawOnChartArea: false },
          }
        }

        return {
          responsive: true,
          interaction: { mode: "index", intersect: false },
          color: textColor,
          plugins: {
            tooltip: this.tooltipLabelCallback
              ? { callbacks: { label: this.tooltipLabelCallback } }
              : {},
            legend: {
              labels: {
                color: textColor,
                usePointStyle: true,
                pointStyle: "line",
              },
            },
          },
          scales,
        }
      },
    },
  })
</script>
